import { format } from 'date-fns';
import { supabase } from '../supabase';

/**
 * Standalone (booking-less) invoice revenue for sales reporting.
 *
 * Every sales/revenue report in the app was historically booking-based; this
 * lib is the single source for folding independent invoices into those
 * numbers so each report doesn't reinvent the query.
 *
 * Conventions (mirrors booking accrual math):
 *   • a "sale" is an ISSUED invoice (status not draft/void)
 *   • amount = (subtotal_cents - discount_cents) / 100 — pre-tax, matching
 *     bookings' `subtotal - discount_amount`
 *   • the sale date is `finalized_at` (issue date), local calendar day
 */

export interface StandaloneInvoiceSale {
  id: string;
  /** ISO timestamp the invoice was issued. */
  finalizedAt: string;
  /** Pre-tax revenue in dollars (subtotal − discount). */
  amount: number;
  /** Pre-discount subtotal in dollars. */
  subtotal: number;
  /** Invoice grand total in dollars (incl. tax), for cash-ish displays. */
  total: number;
  /** Amount collected so far in dollars. */
  paid: number;
  status: string;
}

/**
 * Issued standalone invoices for a business, optionally within a
 * [start, end] window on the issue date. Returns [] on any error so a
 * reporting surface never breaks because of this addition.
 */
export async function fetchStandaloneInvoiceSales(
  businessId: string,
  opts: { start?: Date | string; end?: Date | string } = {},
): Promise<StandaloneInvoiceSale[]> {
  if (!businessId || !supabase) return [];
  try {
    let query = supabase
      .from('invoices')
      .select('id, subtotal_cents, discount_cents, total_cents, amount_paid_cents, status, finalized_at')
      .eq('business_id', businessId)
      .is('booking_id', null)
      .is('proposal_id', null)
      .not('status', 'in', '("draft","void")')
      .not('finalized_at', 'is', null);

    if (opts.start) query = query.gte('finalized_at', toIso(opts.start));
    if (opts.end) query = query.lte('finalized_at', toIso(opts.end));

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((r: any) => ({
      id: r.id,
      finalizedAt: r.finalized_at,
      amount: Math.max(0, ((r.subtotal_cents || 0) - (r.discount_cents || 0)) / 100),
      subtotal: (r.subtotal_cents || 0) / 100,
      total: (r.total_cents || 0) / 100,
      paid: (r.amount_paid_cents || 0) / 100,
      status: r.status,
    }));
  } catch (e) {
    console.warn('[standaloneInvoiceSales] fetch failed (excluded from report):', e);
    return [];
  }
}

function toIso(d: Date | string): string {
  return d instanceof Date ? d.toISOString() : d;
}

/** Σ pre-tax amounts. */
export function sumStandaloneInvoiceSales(rows: StandaloneInvoiceSale[]): number {
  return rows.reduce((s, r) => s + r.amount, 0);
}

/** Bucket pre-tax amounts by local calendar day ('yyyy-MM-dd'). */
export function standaloneSalesByDay(rows: StandaloneInvoiceSale[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const key = format(new Date(r.finalizedAt), 'yyyy-MM-dd');
    out[key] = (out[key] || 0) + r.amount;
  }
  return out;
}

/** Bucket pre-tax amounts by local month index (0–11). */
export function standaloneSalesByMonth(rows: StandaloneInvoiceSale[]): Record<number, number> {
  const out: Record<number, number> = {};
  for (const r of rows) {
    const m = new Date(r.finalizedAt).getMonth();
    out[m] = (out[m] || 0) + r.amount;
  }
  return out;
}
